import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Flag, ChevronDown } from 'lucide-react'
import { getAdminReports, updateReportStatus, type AdminReport } from '../../services/api'

const STATUS_OPTIONS = ['open', 'reviewed', 'resolved', 'dismissed'] as const
const CATEGORY_OPTIONS = ['wrong_location', 'unsafe', 'clue_error', 'offensive_content', 'other'] as const

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  reviewed: 'Reviewed',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
}

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-red-100 text-red-700',
  reviewed: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-700',
  dismissed: 'bg-stone-100 text-stone-600',
}

const CATEGORY_LABELS: Record<string, string> = {
  wrong_location: 'Wrong location',
  unsafe: 'Unsafe area',
  clue_error: 'Clue error',
  offensive_content: 'Offensive content',
  other: 'Other',
}

function ReportRow({ report, onUpdate }: { report: AdminReport; onUpdate: () => void }) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [newStatus, setNewStatus] = useState(report.status)
  const [adminNote, setAdminNote] = useState(report.admin_note || '')

  const mutation = useMutation({
    mutationFn: () => updateReportStatus(report.id, { status: newStatus, admin_note: adminNote || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-reports'] })
      setExpanded(false)
      onUpdate()
    },
  })

  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-stone-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[report.status] || 'bg-stone-100 text-stone-600'}`}>
              {STATUS_LABELS[report.status] || report.status}
            </span>
            <span className="text-xs text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">
              {CATEGORY_LABELS[report.category] || report.category}
            </span>
          </div>
          <p className="font-medium text-stone-800 truncate">{report.hunt_title}</p>
          {report.waypoint_place_name && (
            <p className="text-sm text-stone-500">Waypoint: {report.waypoint_place_name}</p>
          )}
          <p className="text-sm text-stone-500 mt-0.5">
            Reported by <span className="font-medium">{report.user_display_name}</span>
            {' · '}
            {new Date(report.created_at).toLocaleDateString()}
          </p>
          {report.description && (
            <p className="text-sm text-stone-600 mt-1 line-clamp-2">{report.description}</p>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-stone-400 flex-shrink-0 mt-1 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-stone-200 p-4 bg-stone-50 space-y-3">
          {report.description && (
            <div>
              <p className="text-xs font-medium text-stone-500 mb-1">Description</p>
              <p className="text-sm text-stone-700">{report.description}</p>
            </div>
          )}
          {report.admin_note && (
            <div>
              <p className="text-xs font-medium text-stone-500 mb-1">Previous admin note</p>
              <p className="text-sm text-stone-700">{report.admin_note}</p>
            </div>
          )}
          <div className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-32">
              <label className="block text-xs font-medium text-stone-500 mb-1">Update status</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full border border-stone-300 rounded-md px-2 py-1.5 text-sm bg-white"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div className="flex-[2] min-w-48">
              <label className="block text-xs font-medium text-stone-500 mb-1">Admin note</label>
              <input
                type="text"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Optional note..."
                className="w-full border border-stone-300 rounded-md px-2 py-1.5 text-sm bg-white"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setExpanded(false)}
              className="text-sm text-stone-500 hover:text-stone-700 px-3 py-1.5"
            >
              Cancel
            </button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="bg-stone-800 text-white text-sm font-medium px-4 py-1.5 rounded-md hover:bg-stone-900 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Reports() {
  const [statusFilter, setStatusFilter] = useState('open')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-reports', statusFilter, categoryFilter, page],
    queryFn: () => getAdminReports({
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
      page,
    }),
  })

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Flag className="w-5 h-5 text-stone-600" />
        <h1 className="text-xl font-semibold text-stone-800">Reports</h1>
        {data && <span className="text-sm text-stone-500">({data.total} total)</span>}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
          className="border border-stone-300 rounded-md px-3 py-1.5 text-sm bg-white"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(1) }}
          className="border border-stone-300 rounded-md px-3 py-1.5 text-sm bg-white"
        >
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
          ))}
        </select>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12 text-stone-400">Loading...</div>
      )}

      {!isLoading && data?.reports.length === 0 && (
        <div className="text-center py-12 text-stone-400">
          <Flag className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>No reports found</p>
        </div>
      )}

      {data && data.reports.length > 0 && (
        <div className="space-y-3">
          {data.reports.map((r) => (
            <ReportRow key={r.id} report={r} onUpdate={() => {}} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.total > data.page_size && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-3 py-1.5 text-sm border border-stone-300 rounded-md disabled:opacity-40 hover:bg-stone-50"
          >
            Previous
          </button>
          <span className="px-3 py-1.5 text-sm text-stone-500">
            Page {page} of {Math.ceil(data.total / data.page_size)}
          </span>
          <button
            disabled={page >= Math.ceil(data.total / data.page_size)}
            onClick={() => setPage(page + 1)}
            className="px-3 py-1.5 text-sm border border-stone-300 rounded-md disabled:opacity-40 hover:bg-stone-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
